import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Box, Typography, Divider, CircularProgress, Alert,
} from '@mui/material';
import { Send as SendIcon, Edit as EditIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import useAtsSenderName from '../../hooks/useAtsSenderName';
import AtsErpRecordSection from './AtsErpRecordSection';

/**
 * Modal de contratación (etapa "Contratado"). Todo en uno:
 * fecha de ingreso + sueldo bruto (se guardan en la postulación), los datos de
 * recepción, la vista previa del correo y el envío. Se abre desde el botón de la tarjeta.
 * Variables del correo: {{candidato}}, {{cargo}}, {{empresa}}, {{fechaIngreso}},
 * {{sueldoBruto}} (automáticas) y {{hora}}, {{direccion}}, {{recibe}}, {{contacto}}.
 */
const AtsHireDialog = ({ open, onClose, application, positionName = '', orgName = '', onHireChange }) => {
  const { t } = useTranslation();
  const { organization, project, authToken, showAlert } = useStore.getState();

  const [hireStartDate, setHireStartDate] = useState('');
  const [hireGrossSalary, setHireGrossSalary] = useState('');

  // Datos de recepción (se ingresan al enviar)
  const [recibe, setRecibe] = useState('');
  const [contacto, setContacto] = useState('');
  const [direccion, setDireccion] = useState('');
  const [hora, setHora] = useState('');

  const [template, setTemplate] = useState({ subject: '', body: '' });
  const [loadingTpl, setLoadingTpl] = useState(false);
  const [sending, setSending] = useState(false);
  // Si el correo ya fue enviado, se muestra la confirmación primero; el form
  // (campos + envío) aparece al tocar "Volver a enviar el correo".
  const [showForm, setShowForm] = useState(false);
  const { senderName, setSenderName } = useAtsSenderName(open);

  useEffect(() => {
    if (!open) return;
    setHireStartDate(application?.hireStartDate || '');
    setHireGrossSalary(application?.hireGrossSalary || '');
    setRecibe(''); setContacto(''); setDireccion(''); setHora('');
    // Si ya se envió, arrancar mostrando la confirmación (no el form)
    setShowForm(!application?.hireEmailSentAt);

    const loadTemplate = async () => {
      try {
        setLoadingTpl(true);
        const res = await api.get(`/projects/ats-hiring-email`, {
          params: { organizationId: organization, projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = res.data.response?.data;
        if (data) setTemplate({ subject: data.subject || '', body: data.body || '' });
      } catch (err) {
        console.error(err);
        showAlert(t('ats.hire.email.loadError'), 'error');
      } finally {
        setLoadingTpl(false);
      }
    };
    loadTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, application]);

  const candidato = application?.candidateName || application?.candidateEmail || '';

  const resolve = (text) => String(text || '')
    .replace(/\{\{\s*candidato\s*\}\}/g, candidato)
    .replace(/\{\{\s*cargo\s*\}\}/g, positionName || '')
    .replace(/\{\{\s*empresa\s*\}\}/g, orgName || '')
    .replace(/\{\{\s*fechaIngreso\s*\}\}/g, hireStartDate || '')
    .replace(/\{\{\s*sueldoBruto\s*\}\}/g, hireGrossSalary || '')
    .replace(/\{\{\s*hora\s*\}\}/g, hora || '')
    .replace(/\{\{\s*direccion\s*\}\}/g, direccion || '')
    .replace(/\{\{\s*recibe\s*\}\}/g, recibe || '')
    .replace(/\{\{\s*contacto\s*\}\}/g, contacto || '');

  const resolvedSubject = resolve(template.subject);
  const resolvedBody = resolve(template.body);

  const trim = (v) => String(v || '').trim();
  const missing = [];
  if (!trim(hireStartDate)) missing.push(t('ats.hire.startDate'));
  if (!trim(hireGrossSalary)) missing.push(t('ats.hire.grossSalary'));
  if (!trim(recibe)) missing.push(t('ats.hire.email.receiver'));
  if (!trim(contacto)) missing.push(t('ats.hire.email.contact'));
  if (!trim(direccion)) missing.push(t('ats.hire.email.address'));
  if (!trim(hora)) missing.push(t('ats.hire.email.time'));
  const canSend = missing.length === 0;

  const persistHireData = async () => {
    await api.patch(
      `/applications/${application.uid || application.id}/hire-data`,
      { hireStartDate: hireStartDate || null, hireGrossSalary: hireGrossSalary || null },
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
  };

  const handleSend = async () => {
    if (!application || !canSend) return;
    setSending(true);
    try {
      // Guardar los datos de contratación y luego enviar el correo
      await persistHireData();
      const res = await api.post(
        `/applications/${application.uid || application.id}/hire-email`,
        { emailSubject: resolvedSubject, emailBody: resolvedBody, recibe, contacto, direccion, hora, senderName },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.data.response?.status_response === 'OK') {
        const sentAt = res.data.response?.data?.hireEmailSentAt || new Date().toISOString();
        if (onHireChange) onHireChange(application, { hireStartDate, hireGrossSalary, hireEmailSentAt: sentAt });
        showAlert(t('ats.hire.email.sent'), 'success');
        onClose();
      } else {
        showAlert(t('ats.hire.email.sendError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.hire.email.sendError'), 'error');
    } finally {
      setSending(false);
    }
  };

  const formatSentAt = (ts) => {
    if (!ts) return '';
    try {
      if (typeof ts === 'object' && ts._seconds) return new Date(ts._seconds * 1000).toLocaleString('es-CL');
      const d = new Date(ts);
      return isNaN(d.getTime()) ? '' : d.toLocaleString('es-CL');
    } catch (e) {
      return '';
    }
  };

  if (!application) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('ats.hire.title')}</DialogTitle>
      <DialogContent>
        {application.hireEmailSentAt && (
          <Alert severity="success" sx={{ mb: showForm ? 2 : 0 }}>
            {t('ats.hire.lastSent')} {formatSentAt(application.hireEmailSentAt)}
            {application.hireEmail?.sentBy ? ` — ${application.hireEmail.sentBy}` : ''}
          </Alert>
        )}

        {!showForm ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('ats.hire.resendHint')}
          </Typography>
        ) : (
          <>
            <TextField
              fullWidth size="small" type="date"
              label={t('ats.hire.startDate')}
              InputLabelProps={{ shrink: true }}
              value={hireStartDate}
              onChange={(e) => setHireStartDate(e.target.value)}
              sx={{ mb: 2, mt: 1 }}
            />
            <TextField
              fullWidth size="small"
              label={t('ats.hire.grossSalary')}
              placeholder={t('ats.hire.grossSalaryPlaceholder')}
              value={hireGrossSalary}
              onChange={(e) => setHireGrossSalary(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Divider sx={{ mb: 2 }} />

            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
              {t('ats.hire.email.receptionData')}
            </Typography>
            <TextField fullWidth size="small" required sx={{ mb: 1.5 }}
              label={t('ats.hire.email.receiver')} value={recibe}
              onChange={(e) => setRecibe(e.target.value)} />
            <TextField fullWidth size="small" required sx={{ mb: 1.5 }}
              label={t('ats.hire.email.contact')} value={contacto}
              onChange={(e) => setContacto(e.target.value)} />
            <TextField fullWidth size="small" required sx={{ mb: 1.5 }}
              label={t('ats.hire.email.address')} value={direccion}
              onChange={(e) => setDireccion(e.target.value)} />
            <TextField fullWidth size="small" required sx={{ mb: 2 }}
              label={t('ats.hire.email.time')} placeholder="09:00" value={hora}
              onChange={(e) => setHora(e.target.value)} />

            <Divider sx={{ mb: 2 }} />

            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
              {t('ats.hire.email.preview')}
            </Typography>
            {loadingTpl ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <>
                <TextField fullWidth size="small" sx={{ mb: 1.5 }}
                  label={t('ats.config.emailSender.fieldLabel')} value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  inputProps={{ maxLength: 80 }} />
                <TextField fullWidth size="small" sx={{ mb: 1.5 }}
                  label={t('ats.hire.email.subject')} value={resolvedSubject}
                  InputProps={{ readOnly: true }} />
                <TextField fullWidth multiline minRows={8}
                  label={t('ats.hire.email.body')} value={resolvedBody}
                  InputProps={{ readOnly: true }} />
              </>
            )}

            {!canSend && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1.5 }}>
                {t('ats.hire.email.missing')}: {missing.join(', ')}
              </Typography>
            )}
          </>
        )}
        {/* Puente ERP: crear ficha de colaborador desde esta contratación */}
        <AtsErpRecordSection application={application} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} color="inherit">{t('cancel')}</Button>
        {showForm ? (
          <Button onClick={handleSend} variant="contained" disabled={sending || !canSend}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}>
            {t('ats.hire.sendEmail')}
          </Button>
        ) : (
          <Button onClick={() => setShowForm(true)} variant="contained"
            startIcon={<EditIcon />}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}>
            {t('ats.hire.resend')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AtsHireDialog;
